/**
 * 站內提醒 toast（2026-08-27）— 右下角堆疊，可點擊跳轉，數秒後自動收起。
 * 行動裝置改為底部橫向滿版，避免擋住操作區。
 */
import { useEffect } from "react";
import { useLocation } from "wouter";
import { Bell, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useNotifications, type ToastItem } from "../../contexts/NotificationContext";

const AUTO_DISMISS_MS = 8000;

export function NotificationToaster() {
  const { toasts } = useNotifications();
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-3 bottom-3 z-50 flex flex-col gap-2 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[360px]"
    >
      {toasts.map((t) => (
        <Toast key={t.id} item={t} />
      ))}
    </div>
  );
}

function Toast({ item }: { item: ToastItem }) {
  const { t } = useLanguage();
  const [, setLocation] = useLocation();
  const { dismiss, markRead } = useNotifications();

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(item.id), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [item.id, dismiss]);

  const open = () => {
    markRead(item.id);
    if (item.path) setLocation(item.path);
  };

  return (
    <div
      role="status"
      className="pointer-events-auto animate-fade-up overflow-hidden rounded-lg border border-border bg-card shadow-[0_8px_24px_oklch(0%_0_0/0.12)]"
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-soft">
          <Bell className="h-3.5 w-3.5 text-primary-soft-foreground" />
        </div>
        <button
          type="button"
          onClick={open}
          className="min-w-0 flex-1 space-y-0.5 text-left"
          title={item.path ? t({ "zh-TW": "點擊查看", "en-US": "Click to view", "zh-CN": "点击查看" }) : undefined}
        >
          <p className="truncate text-[13px] font-medium">{item.title}</p>
          <p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">{item.body}</p>
          {item.path && (
            <p className="pt-0.5 text-[12px] text-primary">
              {t({ "zh-TW": "查看詳情 →", "en-US": "View details →", "zh-CN": "查看详情 →" })}
            </p>
          )}
        </button>
        <button
          type="button"
          onClick={() => dismiss(item.id)}
          aria-label={t({ "zh-TW": "關閉提醒", "en-US": "Dismiss", "zh-CN": "关闭提醒" })}
          className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
